/**
 * FirecrawlBudgetPanel — iter136: admin controls for the shared Firecrawl pool.
 * Throttle (budget brake) + Boost (spend surplus near the monthly reset) with
 * live state. Mutual exclusion is server-enforced (409 when both requested).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Flame, Rocket, Turtle } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../config";
import { Switch } from "../ui/switch";

export default function FirecrawlBudgetPanel({ dm }) {
  const [throttle, setThrottle] = useState(null); // {enabled, updated_by, usage}
  const [boost, setBoost] = useState(null);       // {enabled, daily_cap, credits_today, active, ...}
  const [capInput, setCapInput] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, b] = await Promise.all([
        axios.get(`${API}/admin/firecrawl-throttle`),
        axios.get(`${API}/admin/firecrawl-boost`),
      ]);
      setThrottle(t.data);
      setBoost(b.data);
      setCapInput(String(b.data.daily_cap ?? 150));
    } catch { /* card shows loading state */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flip = async (kind, enabled, extra = {}) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/firecrawl-${kind}`, { enabled, ...extra });
      toast.success(`Firecrawl ${kind} ${enabled ? "enabled" : "disabled"}`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : `Could not update ${kind}`);
    } finally {
      setBusy(false);
    }
  };

  const saveCap = async () => {
    const cap = parseInt(capInput, 10);
    if (!Number.isFinite(cap) || cap < 10 || cap > 2000) {
      toast.error("Daily cap must be between 10 and 2000 credits");
      return;
    }
    await flip("boost", !!boost?.enabled, { daily_cap: cap });
  };

  const usage = throttle?.usage;
  const boostBadge = !boost?.enabled
    ? { label: "Off", bg: dm ? "rgba(148,163,184,0.15)" : "#f1f4f5", fg: dm ? "#94a3b8" : "#5f6b6d" }
    : boost.active
      ? { label: "Active", bg: "#e7f6ec", fg: "#166534" }
      : { label: boost.daily_cap_reached ? "Suspended — daily cap reached" : "Suspended — throttle on", bg: "#fef6e0", fg: "#92400e" };

  const rowCls = `flex flex-wrap items-center justify-between gap-3 py-3 border-b ${dm ? "border-white/5" : "border-black/5"}`;

  return (
    <div data-testid="firecrawl-budget-panel" className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`}
      style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" }}>
      <div className="flex items-center gap-2 mb-1">
        <Flame className="w-4 h-4 text-[#e0651a]" />
        <h3 className={`editorial-heading text-xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Firecrawl Budget</h3>
      </div>
      {usage ? (
        <p data-testid="firecrawl-usage-summary" className={`text-xs mb-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          {usage.credits_used} credits used this cycle · resets {usage.cycle_reset}
          {boost != null && <> · {boost.credits_today} today</>}
        </p>
      ) : (
        <p className={`text-xs mb-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Loading…</p>
      )}

      <div className={rowCls}>
        <div className="flex items-start gap-3">
          <Turtle className={`w-4 h-4 mt-0.5 ${dm ? "text-amber-500" : "text-amber-600"}`} />
          <div>
            <p className={`text-sm font-medium ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Throttle (budget brake)</p>
            <p className={`text-[11px] ${dm ? "text-slate-500" : "text-[#9aa7a9]"}`}>
              Discovery biweekly · enrichment tier-3 off · html cadence 48h
              {throttle?.updated_by && <> · last flip: {throttle.updated_by}</>}
            </p>
          </div>
        </div>
        <Switch data-testid="firecrawl-throttle-toggle" disabled={busy || !throttle}
          checked={!!throttle?.enabled}
          onCheckedChange={(v) => flip("throttle", v)} />
      </div>

      <div className={`${rowCls} border-b-0`}>
        <div className="flex items-start gap-3">
          <Rocket className={`w-4 h-4 mt-0.5 ${dm ? "text-sky-400" : "text-[#006874]"}`} />
          <div>
            <p className={`text-sm font-medium flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
              Boost (spend surplus)
              <span data-testid="firecrawl-boost-status" className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                style={{ background: boostBadge.bg, color: boostBadge.fg }}>{boostBadge.label}</span>
            </p>
            <p className={`text-[11px] ${dm ? "text-slate-500" : "text-[#9aa7a9]"}`}>
              Discovery daily · enrichment batch ×2 · source scans ×2
              {boost?.updated_by && <> · last flip: {boost.updated_by}</>}
            </p>
            <div className="flex items-center gap-2 mt-2">
              <label className={`text-[11px] ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Daily cap</label>
              <input data-testid="firecrawl-boost-cap-input" type="number" min={10} max={2000}
                value={capInput} onChange={(e) => setCapInput(e.target.value)}
                className={`w-20 text-xs px-2 py-1 rounded-lg outline-none ${dm ? "bg-white/5 text-slate-200" : "bg-[#f1f4f5] text-[#191c1d]"}`} />
              <button data-testid="firecrawl-boost-cap-save" type="button" disabled={busy || !boost}
                onClick={saveCap}
                className={`text-[11px] font-semibold px-3 py-1 rounded-full disabled:opacity-40 ${dm ? "bg-white/10 text-slate-200" : "bg-[#eef1f2] text-[#3b494c]"}`}>
                Save
              </button>
              {boost != null && (
                <span className={`text-[11px] ${dm ? "text-slate-600" : "text-[#9aa7a9]"}`}>
                  {boost.credits_today}/{boost.daily_cap} today
                </span>
              )}
            </div>
          </div>
        </div>
        <Switch data-testid="firecrawl-boost-toggle" disabled={busy || !boost}
          checked={!!boost?.enabled}
          onCheckedChange={(v) => flip("boost", v)} />
      </div>
    </div>
  );
}
